import type { Container, DestroyOptions } from 'pixi.js';
import { getOverlayCanvas } from './domOverlay';

/** True only when a display object and every ancestor are visible enough to be interactive. */
export function isEffectivelyVisible(owner: Container): boolean {
  let node: Container | null = owner;
  while (node) {
    if (!node.visible || node.alpha <= 0.02) return false;
    node = node.parent;
  }
  return true;
}

/**
 * Mirrors a Pixi control into the DOM accessibility tree. The element does not intercept pointer
 * input (touch continues to hit Pixi), but VoiceOver and keyboard users can focus and activate it.
 */
export class AccessibleButton {
  readonly el: HTMLButtonElement;
  private raf = 0;
  private gone = false;

  constructor(
    private readonly owner: Container,
    private readonly width: number,
    private readonly height: number,
    label: string,
    onActivate: () => void,
  ) {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = 'pixi-a11y-control';
    el.setAttribute('aria-label', label);
    el.addEventListener('click', onActivate);
    document.body.appendChild(el);
    this.el = el;

    const tick = (): void => {
      if (this.gone) return;
      this.reposition();
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  setLabel(label: string): void {
    this.el.setAttribute('aria-label', label);
  }

  setDisabled(disabled: boolean): void {
    this.el.disabled = disabled;
    this.el.setAttribute('aria-disabled', String(disabled));
  }

  private reposition(): void {
    const canvas = getOverlayCanvas();
    if (!canvas || !this.owner.parent || !isEffectivelyVisible(this.owner)) {
      this.el.style.display = 'none';
      return;
    }

    const origin = this.owner.toGlobal({ x: 0, y: 0 });
    const right = this.owner.toGlobal({ x: this.width, y: 0 });
    const bottom = this.owner.toGlobal({ x: 0, y: this.height });
    const canvasRect = canvas.getBoundingClientRect();
    this.el.style.display = 'block';
    this.el.style.left = `${canvasRect.left + Math.min(origin.x, right.x)}px`;
    this.el.style.top = `${canvasRect.top + Math.min(origin.y, bottom.y)}px`;
    this.el.style.width = `${Math.max(1, Math.abs(right.x - origin.x))}px`;
    this.el.style.height = `${Math.max(1, Math.abs(bottom.y - origin.y))}px`;
  }

  destroy(_options?: DestroyOptions): void {
    this.gone = true;
    cancelAnimationFrame(this.raf);
    this.el.remove();
  }
}

/** Announce asynchronous status changes without forcing focus away from the current control. */
export function announceStatus(message: string, assertive = false): void {
  const el = document.getElementById('pixi-live-region');
  if (!el) return;
  el.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
  el.textContent = '';
  requestAnimationFrame(() => {
    el.textContent = message;
  });
}
